import { useMutation, useQuery } from '@apollo/client';
import { useContext, useMemo } from 'react';
import { ActiveDatabaseContext } from '../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import {
  RapportSettingsQuery,
  RapportSettingsQueryVariables,
  RapportSettingsQuery_me_rapportSettings_values_value_TEDMixedString,
} from './Queries/RapportSettingsQuery/_QueryTypes/RapportSettingsQuery';
import rapportSettingsQuery from './Queries/RapportSettingsQuery/RapportSettingsQuery.gql';
import {
  RapportSettingsMutation,
  RapportSettingsMutationVariables,
} from './Queries/RapportSettingsMutation/_QueryTypes/RapportSettingsMutation';
import rapportSettingsMutation from './Queries/RapportSettingsMutation/RapportSettingsMutation.gql';
import { TEDInputSettingsWriteSection } from '../../../globalQueryTypes';

export type RapportSettings = { [key: string]: string };

export const useReadRapportSettings: (
  rapportID: string
) => RapportSettings = rapportID => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const rapportSettingsPath = `Forms/${rapportID}`;
  const options = useMemo(
    () => ({
      variables: {
        dbName: activeDatabaseContext.activeDatabase || 'noDB',
        rapportSection: rapportSettingsPath,
      },
      skip: !activeDatabaseContext.activeDatabase,
    }),
    [activeDatabaseContext.activeDatabase, rapportSettingsPath]
  );

  const { data, loading } = useQuery<
    RapportSettingsQuery,
    RapportSettingsQueryVariables
  >(rapportSettingsQuery, options);
  return useMemo(
    () =>
      (data &&
        !loading &&
        data.me
          ?.at(0)
          ?.rapportSettings?.at(0)
          ?.values?.reduce<Record<string, string>>((settings, value) => {
            const name = value.name;
            const strValue = (
              value.value as RapportSettingsQuery_me_rapportSettings_values_value_TEDMixedString
            ).asString;
            if (name && strValue) {
              settings[name] = strValue;
            }
            return settings;
          }, {})) ||
      {},
    [data, loading]
  );
};

export const useWriteRapportSettings = (rapportID: string) => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const [mutation] = useMutation<
    RapportSettingsMutation,
    RapportSettingsMutationVariables
  >(rapportSettingsMutation);
  const section = `Forms/${rapportID}`;
  const dbName = activeDatabaseContext.activeDatabase || 'noDB';
  return (settings: RapportSettings) => {
    const rapportSettings: TEDInputSettingsWriteSection = {
      name: section,
      values: Object.keys(settings).map(name => ({
        name: name,
        asString: settings[name],
      })),
    };
    if (dbName) {
      mutation({
        variables: {
          dbName: dbName,
          rapportSection: rapportSettings,
        },
      });
    }
  };
};
